import { XDMWrapper, TARGETS } from '@gt-common/event-logger-web';
import { RailsLandingPageSchema } from './railsSchemas';
import { store } from '../redux/store';
import { pdtClientSchemaV2 } from '../constants/AbConfigKeys';
import { FUNNEL_SOURCE } from '../constants/TrainConstants';

export const setFunnelSource = () => {
  if (typeof window === 'undefined') return FUNNEL_SOURCE.DIRECT;
  const urlParams = new URLSearchParams(window.location.search);
  let source = FUNNEL_SOURCE.DIRECT;
  if (urlParams.get(FUNNEL_SOURCE.SEO_PARAM) === 'true') {
    source = FUNNEL_SOURCE.SEO;
  } else if (urlParams.get(FUNNEL_SOURCE.SEM_PARAM)?.toUpperCase().startsWith(FUNNEL_SOURCE.SEM)) {
    source = FUNNEL_SOURCE.SEM;
  }
  sessionStorage.setItem(FUNNEL_SOURCE.STORAGE_KEY, source);
  return source;
};

export const getFunnelSource = () => {
  if (typeof window === 'undefined') return FUNNEL_SOURCE.DIRECT;
  return sessionStorage.getItem(FUNNEL_SOURCE.STORAGE_KEY) || FUNNEL_SOURCE.DIRECT;
};

export const PDT_SCHEMA_ID_DEFAULT = 167325;
export const PDT_SCHEMA_NAME_DEFAULT = "mmt_rails_b2c_pdt_client_logging";

export const PDT_SCHEMA_ID_V2 = 173469;
export const PDT_SCHEMA_NAME_V2 = "mmt_rails_b2c_pdt_client_logging_v2";

export const getV2PDTclientSchema = () => {
  const state = store?.getState?.();
  return state?.abConfig?.[pdtClientSchemaV2] === true;
};

export const getPdtSchemaId = () => {
  return getV2PDTclientSchema() ? PDT_SCHEMA_ID_V2 : PDT_SCHEMA_ID_DEFAULT;
};

export const getPdtSchemaName = () => {
  return getV2PDTclientSchema() ? PDT_SCHEMA_NAME_V2 : PDT_SCHEMA_NAME_DEFAULT;
};

export const PDT_SCHEMA_ID = PDT_SCHEMA_ID_DEFAULT;
export const PDT_SCHEMA_NAME = PDT_SCHEMA_NAME_DEFAULT;

export const RAILS_CONSTANTS = {
  PAGE_TYPE: 'funnel',
  PAGE_NAME: 'rails-listing',
  FUNNEL_STEP: 'listing',
  LOB: 'rails',
  LOB_CATEGORY: 'rails',
  TRAVEL_CLASS: 'listing',
  STORE_ID: 'MMT',
  ORGANISATION: 'MMT',
  BU: 'ground_transport',
  get TEMPLATE_ID() { return getPdtSchemaId(); },
  get TOPIC_NAME() { return getPdtSchemaName(); },
  TIMEZONE: 'IST',
  get FUNNEL_SOURCE() { return getFunnelSource(); },
  COUNTRY: 'India',
  COUNTRY_CODE: 'IN',
  LOCUS_TYPE: 'CITY',
  CATEGORY: 'rails'
};

export const generateUUID = () => {
  let d = new Date().getTime();
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    d += performance.now();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const buildPageContext = ({
  pageType = 'funnel',
  pageName,
  funnelStep,
  lob = 'rails',
  lobCategory = 'rails',
}) => ({
  page_type: pageType,
  page_name: pageName,
  funnel_step: funnelStep,
  lob,
  lob_category: lobCategory,
  travel_store: {
    store_id: 'MMT'
  }
});

export const getAdvancePurchaseDays = (dateStr) => {
  if (!dateStr) return '0';
  const year = parseInt(dateStr.substring(0, 4), 10);
  const month = parseInt(dateStr.substring(4, 6), 10) - 1;
  const day = parseInt(dateStr.substring(6, 8), 10);
  const travelDate = new Date(year, month, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return String(Math.floor((travelDate - today) / 86400000));
};

export const buildSearchContext = (fromStation, toStation, travelClass = 'listing') => ({
  from: {
    locus: {
      locus_id: fromStation?.locusCode || fromStation?.code || '',
      locus_type: fromStation?.locusType || 'CITY',
      country: 'India',
      locus_v2_id: fromStation?.locusV2Id || '',
      locus_v2_type: fromStation?.locusV2Type || '',
    },
    location: {
      code: fromStation?.code || '',
      name: fromStation?.city || fromStation?.name || '',
      country_code: 'IN',
      country: 'India'
    }
  },
  to: {
    locus: {
      locus_id: toStation?.locusCode || toStation?.code || '',
      locus_type: toStation?.locusType || 'CITY',
      country: 'India',
      locus_v2_id: toStation?.locusV2Id || '',
      locus_v2_type: toStation?.locusV2Type || '',
    },
    location: {
      code: toStation?.code || '',
      name: toStation?.city || toStation?.name || '',
      country_code: 'IN',
      country: 'India'
    }
  },
  travel_class: travelClass
});

export const buildEventTrackingContext = ({
  sessionId,
} = {}) => ({
  organisation: RAILS_CONSTANTS.ORGANISATION,
  bu: RAILS_CONSTANTS.BU,
  env: process.env.NODE_ENV === 'development' ? 'development' : 'production',
  template_id: RAILS_CONSTANTS.TEMPLATE_ID,
  topic_name: RAILS_CONSTANTS.TOPIC_NAME,
  timezone: RAILS_CONSTANTS.TIMEZONE,
  funnel_source: RAILS_CONSTANTS.FUNNEL_SOURCE,
  ...(sessionId && { session_id: sessionId }),
});

export const buildEventDetail = ({
  eventName,
  eventType,
  eventValue,
  components,
  bookingInfo,
  addonDetails,
  travellerInfo,
}) => {
  const detail = {
    event_id: generateUUID(),
    event_timestamp: Date.now(),
    event_name: eventName,
    event_type: eventType,
    event_value: eventValue,
  };

  if (components) {
    detail.components = components;
  }

  if (bookingInfo) {
    detail.booking_info = bookingInfo;
  }

  if (addonDetails) {
    detail.addon_details = addonDetails;
  }

  if (travellerInfo) {
    detail.traveller_info = travellerInfo;
  }

  return detail;
};

export const parseAmount = (amount) => {
  if (typeof amount === 'number') return amount;
  if (typeof amount === 'string') {
    const parsed = parseFloat(amount.replace(/[^0-9.]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const LOB_NAME = 'rails';

export const isXdmTrackingEnabled = (isXdmEnabled) => {
  if (isXdmEnabled === false) {
    return false;
  }
  return true;
};

export const sendXDMEvent = async ({ payload, eventName = 'XDM Event', isXdmEnabled = false }) => {
  try {
    if (!isXdmTrackingEnabled(isXdmEnabled)) {
      return;
    }

    await XDMWrapper.logEvent({
      payload,
      schema: RailsLandingPageSchema,
      otherConfig: {
        lobName: LOB_NAME,
        adobeKeyConfig: []
      },
      target: TARGETS.ALL,
      extraOptions: {
        lob: LOB_NAME,
        destinationUrl: typeof window !== 'undefined' ? window.location.href : ''
      }
    });
  } catch (error) {
    console.error(`[${eventName}] Error:`, error.message);
    throw error;
  }
};

export { TARGETS };
